import React, { useEffect, useState } from 'react';
import {
  CCard, CCardBody, CCardHeader, CRow, CCol, CButton,
  CFormLabel, CFormInput, CAlert, CSpinner,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilSave } from '@coreui/icons';
import api from '../services/api';

export default function AdminConfigPage() {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  // SST
  const [sstEnabled, setSstEnabled] = useState(true);
  const [sstRate, setSstRate]   = useState('6');

  // Loyalty
  const [loyaltyEnabled, setLoyaltyEnabled]     = useState(true);
  const [regBonus, setRegBonus]                 = useState('100');
  const [pointsPerRm, setPointsPerRm]           = useState('100');
  const [minJobHigh, setMinJobHigh]             = useState('20');
  const [ptsJobHigh, setPtsJobHigh]             = useState('100');
  const [minJobLow, setMinJobLow]               = useState('10');
  const [ptsJobLow, setPtsJobLow]               = useState('50');

  useEffect(() => {
    api.get('/config')
      .then(({ data }) => {
        const c = data.config || {};
        setSstEnabled(c.sst_enabled !== '0');
        setSstRate(c.sst_rate ?? '6');
        setLoyaltyEnabled(c.loyalty_enabled !== '0');
        setRegBonus(c.loyalty_reg_bonus ?? '100');
        setPointsPerRm(c.loyalty_points_per_rm ?? '100');
        setMinJobHigh(c.loyalty_min_job_rm_high ?? '20');
        setPtsJobHigh(c.loyalty_pts_job_high ?? '100');
        setMinJobLow(c.loyalty_min_job_rm_low ?? '10');
        setPtsJobLow(c.loyalty_pts_job_low ?? '50');
      })
      .catch(() => setError('Failed to load configuration.'))
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const { data } = await api.put('/config', {
        sst_enabled: sstEnabled ? '1' : '0',
        sst_rate: String(sstRate),
        loyalty_enabled:         loyaltyEnabled ? '1' : '0',
        loyalty_reg_bonus:       String(regBonus),
        loyalty_points_per_rm:   String(pointsPerRm),
        loyalty_min_job_rm_high: String(minJobHigh),
        loyalty_pts_job_high:    String(ptsJobHigh),
        loyalty_min_job_rm_low:  String(minJobLow),
        loyalty_pts_job_low:     String(ptsJobLow),
      });
      const c = data.config || {};
      setSstEnabled(c.sst_enabled !== '0');
      setSstRate(c.sst_rate ?? '6');
      setLoyaltyEnabled(c.loyalty_enabled !== '0');
      setRegBonus(c.loyalty_reg_bonus ?? '100');
      setPointsPerRm(c.loyalty_points_per_rm ?? '100');
      setMinJobHigh(c.loyalty_min_job_rm_high ?? '20');
      setPtsJobHigh(c.loyalty_pts_job_high ?? '100');
      setMinJobLow(c.loyalty_min_job_rm_low ?? '10');
      setPtsJobLow(c.loyalty_pts_job_low ?? '50');
      setSuccess('Configuration saved.');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-center py-5"><CSpinner /></div>;

  return (
    <>
      <CRow className="mb-3 align-items-center">
        <CCol><h5 className="mb-0">Global Configuration</h5></CCol>
      </CRow>

      {error   && <CAlert color="danger"  dismissible onClose={() => setError('')}>{error}</CAlert>}
      {success && <CAlert color="success" dismissible onClose={() => setSuccess('')}>{success}</CAlert>}

      <form onSubmit={handleSave}>
        {/* ── SST ── */}
        <CCard className="border-0 shadow-sm mb-4" style={{ borderRadius: 12, maxWidth: 600 }}>
          <CCardHeader className="bg-white fw-semibold">Finance — SST Settings</CCardHeader>
          <CCardBody>
            <div className="mb-4">
              <CFormLabel className="fw-semibold mb-1">SST (Sales & Service Tax)</CFormLabel>
              <div className="d-flex gap-3 mt-1">
                {[true, false].map(val => (
                  <label key={String(val)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="radio" name="sst_enabled" checked={sstEnabled === val} onChange={() => setSstEnabled(val)} />
                    <span>
                      <strong>{val ? 'Enabled' : 'Disabled'}</strong>
                      <div className="text-muted small">{val ? 'SST is calculated on new invoices' : 'No SST applied on new invoices'}</div>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mb-3" style={{ opacity: sstEnabled ? 1 : 0.45, pointerEvents: sstEnabled ? 'auto' : 'none' }}>
              <CFormLabel className="fw-semibold">Default SST Rate (%)</CFormLabel>
              <CFormInput
                type="number" inputMode="decimal" min="0" max="100" step="0.01"
                value={sstRate} onChange={e => setSstRate(e.target.value)}
                style={{ maxWidth: 140 }} disabled={!sstEnabled}
              />
              <div className="text-muted small mt-1">Pre-filled on the Generate Invoice form when SST is enabled.</div>
            </div>
          </CCardBody>
        </CCard>

        {/* ── Loyalty Points ── */}
        <CCard className="border-0 shadow-sm mb-4" style={{ borderRadius: 12, maxWidth: 600 }}>
          <CCardHeader className="bg-white fw-semibold">FMZ Points — Loyalty Program</CCardHeader>
          <CCardBody>
            <div className="mb-4">
              <CFormLabel className="fw-semibold mb-1">Loyalty Program</CFormLabel>
              <div className="d-flex gap-3 mt-1">
                {[true, false].map(val => (
                  <label key={String(val)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input type="radio" name="loyalty_enabled" checked={loyaltyEnabled === val} onChange={() => setLoyaltyEnabled(val)} />
                    <span>
                      <strong>{val ? 'Enabled' : 'Disabled'}</strong>
                      <div className="text-muted small">{val ? 'Customers can earn and redeem points' : 'Loyalty program is inactive'}</div>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ opacity: loyaltyEnabled ? 1 : 0.45, pointerEvents: loyaltyEnabled ? 'auto' : 'none' }}>
              <CRow className="mb-3">
                <CCol sm={6}>
                  <CFormLabel className="fw-semibold">Registration Bonus (pts)</CFormLabel>
                  <CFormInput type="number" min="0" value={regBonus} onChange={e => setRegBonus(e.target.value)} />
                  <div className="text-muted small mt-1">Points awarded on new account registration.</div>
                </CCol>
                <CCol sm={6}>
                  <CFormLabel className="fw-semibold">Points per RM1 Discount</CFormLabel>
                  <CFormInput type="number" min="1" value={pointsPerRm} onChange={e => setPointsPerRm(e.target.value)} />
                  <div className="text-muted small mt-1">e.g. 100 pts = RM1.00 discount.</div>
                </CCol>
              </CRow>

              <div className="mb-2 fw-semibold text-muted small text-uppercase" style={{ letterSpacing: 1 }}>Points Earned per Completed Job</div>

              <CRow className="mb-3">
                <CCol sm={6}>
                  <CFormLabel className="fw-semibold">Min. Job Cost — High Tier (RM)</CFormLabel>
                  <CFormInput type="number" min="0" step="0.01" value={minJobHigh} onChange={e => setMinJobHigh(e.target.value)} />
                </CCol>
                <CCol sm={6}>
                  <CFormLabel className="fw-semibold">Points Earned (High Tier)</CFormLabel>
                  <CFormInput type="number" min="0" value={ptsJobHigh} onChange={e => setPtsJobHigh(e.target.value)} />
                </CCol>
              </CRow>

              <CRow className="mb-1">
                <CCol sm={6}>
                  <CFormLabel className="fw-semibold">Min. Job Cost — Low Tier (RM)</CFormLabel>
                  <CFormInput type="number" min="0" step="0.01" value={minJobLow} onChange={e => setMinJobLow(e.target.value)} />
                </CCol>
                <CCol sm={6}>
                  <CFormLabel className="fw-semibold">Points Earned (Low Tier)</CFormLabel>
                  <CFormInput type="number" min="0" value={ptsJobLow} onChange={e => setPtsJobLow(e.target.value)} />
                </CCol>
              </CRow>
              <div className="text-muted small mt-1 mb-3">
                Jobs costing RM{minJobLow}–RM{minJobHigh} earn {ptsJobLow} pts. Jobs above RM{minJobHigh} earn {ptsJobHigh} pts.
              </div>
            </div>
          </CCardBody>
        </CCard>

        <CButton color="success" type="submit" disabled={saving}>
          {saving ? <CSpinner size="sm" /> : <><CIcon icon={cilSave} className="me-1" />Save All Settings</>}
        </CButton>
      </form>
    </>
  );
}
